import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import * as day from 'dayjs';
import { BmReportsFilters, ReportTypes } from 'libs/domain';
import { ScheduledReport, ScheduledReportFrequency } from 'libs/domain';
import { BehaviorSubject, ReplaySubject, from, of } from 'rxjs';
import { filter, takeUntil, switchMap, map, finalize, catchError } from 'rxjs/operators';

import { RootService } from '@app/src/app/minimal/services/RootService';
import { BsSchedulesService } from '@app/src/app/shared/services/bell-schedule/bs-schedules.service';
import { PbisService } from '@app/src/app/shared/services/pbis';
import { scrollIntoView } from '@app/src/app/util/scroll-into-view';

import { CrudFormBase } from '@shared/components/crud-form-base/crud-form-base.abstract';
import {
  MODAL_OVERLAY_DATA,
  ModalOverlayPrimaryHeaderBackground,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SelectFieldsOption } from '@shared/components/select-fields';
import {
  SystemAlertCloseEvents,
  SystemAlertModalService,
  SystemAlertModalType,
} from '@shared/components/system-alert-modal';
import { ScheduledReportsService } from '@shared/services/scheduled-reports/scheduled-reports.service';

import { BellSchedulePermissionsService } from '../../../mm-bell-schedule/services/bell-schedule-permissions.service';
import {
  PERIODIC_FREQUENCY_OPTION,
  DAY_OPTIONS,
  DELIVERY_OPTIONS,
  NON_PERIODIC_FREQUENCY_OPTIONS,
  REPORT_TYPE_OPTIONS,
  SCHEDULED_REPORTS_EDIT_FORMGROUP,
  SIS_TEMPLATE_OPTIONS,
  ScheduledReportEditFormFields,
  ScheduledReportEditMessage,
  getDayEnum,
  SR_DEFAULT_PERIOD_WAIT_TO_SEND,
} from '../../constants';
import { ScheduledReportsStore } from '../../store/scheduled-reports.store';
import {
  ClientSupportedScheduledReportFilter,
  MmSrReportsEditData,
  MmSrReportsEditResponse,
  ScheduledReportType,
} from '../../types';

@Component({
  selector: 'mg-mm-sr-reports-edit',
  templateUrl: './mm-sr-reports-edit.component.html',
  styleUrls: ['./mm-sr-reports-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ScheduledReportsStore],
})
export class MmSrReportsEditComponent
  extends CrudFormBase<ScheduledReport>
  implements OnInit, OnDestroy, AfterViewInit
{
  private _destroyed$ = new ReplaySubject<void>(1);

  public MESSAGES = ScheduledReportEditMessage;
  public FORM_FIELDS = ScheduledReportEditFormFields;
  public readonly MODAL_CONFIG = {
    headerBg: ModalOverlayPrimaryHeaderBackground.BLUE,
  };
  public readonly SIS_TEMPLATE_OPTIONS = SIS_TEMPLATE_OPTIONS;
  public readonly form = this._fb.group(SCHEDULED_REPORTS_EDIT_FORMGROUP);
  public isNew: boolean;
  public REPORT_TYPE_OPTIONS = REPORT_TYPE_OPTIONS;
  public DELIVERY_OPTIONS = DELIVERY_OPTIONS;
  public DAY_OPTIONS = DAY_OPTIONS;
  public REPORT_TYPES = ScheduledReportType;
  public FREQUENCE_TYPES = ScheduledReportFrequency;
  public emailOptions: SelectFieldsOption<string>[];

  private _defaultFilterValuesSubj = new BehaviorSubject<ClientSupportedScheduledReportFilter>(null);
  public defaultFilterValues$ = this._defaultFilterValuesSubj.asObservable();

  private _frequencyOptSubj = new BehaviorSubject<SelectFieldsOption<ScheduledReportFrequency>[]>(
    NON_PERIODIC_FREQUENCY_OPTIONS,
  );
  public frequencyOptions$ = this._frequencyOptSubj.asObservable();

  private _lastReportType: ReportTypes;
  private _lastFrequencyType: ScheduledReportFrequency;

  public bellScheduleActive$ = from(this._bellSchedulePermService.isBellScheduleEnabledForCurrentUser());
  public bellScheduleOptions$ = this.bellScheduleActive$.pipe(
    switchMap(enabled => {
      if (!enabled) {
        return of([]);
      }
      this._setFormState('loading');
      return from(this._bellScheduleService.fetchAll()).pipe(
        map(schedules => schedules.map(s => ({ label: s.name, value: s.id }))),
        finalize(() => this._setFormState('idle')),
        catchError(err => {
          console.error('Error fetching bell schedules:', err);
          return of([]);
        }),
      );
    }),
  );

  get periodWaitValue(): number {
    return +this.form.get(this.FORM_FIELDS.PERIOD_WAIT_TO_SEND).value || 0;
  }

  @ViewChild('crudForm', { static: false })
  crudForm?: ElementRef<HTMLElement>;

  constructor(
    @Inject(MODAL_OVERLAY_DATA)
    public dialogData: MmSrReportsEditData,
    private _modalRef: ModalOverlayRef<MmSrReportsEditResponse, MmSrReportsEditData>,
    private _fb: UntypedFormBuilder,
    private _systemAlertModal: SystemAlertModalService,
    private _scheduledReportsService: ScheduledReportsService,
    public rootService: RootService,
    private _cdr: ChangeDetectorRef,
    private _bellSchedulePermService: BellSchedulePermissionsService,
    private _bellScheduleService: BsSchedulesService,
    private _pbisService: PbisService,
  ) {
    super({
      id: dialogData?.id,
      get: async id => {
        return this._scheduledReportsService.get(id);
      },
      create: data => {
        return this._scheduledReportsService.create(data as ScheduledReport);
      },
      update: data => {
        return this._scheduledReportsService.update(data as ScheduledReport);
      },
      delete: async data => {
        await this._scheduledReportsService.delete(data.id);
        return data;
      },
      onSetForm: data => {
        const reportType = dialogData?.prepopulatedFields?.reportType || data.reportType;

        this.form.patchValue({
          [ScheduledReportEditFormFields.NAME]: data.name,
          [ScheduledReportEditFormFields.REPORT_TYPE]: reportType,
          [ScheduledReportEditFormFields.FREQUENCY]: data.frequency ?? ScheduledReportFrequency.Daily,
          [ScheduledReportEditFormFields.TIME]: data.time ?? day().set('hour', 15).set('minute', 0).format('HH:00'),
          [ScheduledReportEditFormFields.DAY]: data.day ?? getDayEnum(day()),
          [ScheduledReportEditFormFields.DELIVERY_TYPE]: data.deliveryType,
          [ScheduledReportEditFormFields.EMAIL]: data.email || [],
          [ScheduledReportEditFormFields.SFTP_HOST]: data.sftpHost,
          [ScheduledReportEditFormFields.SFTP_USER_NAME]: data.sftpUserName,
          [ScheduledReportEditFormFields.SFTP_PASSWORD]: data.sftpPassword,
          [ScheduledReportEditFormFields.PERIOD_WAIT_TO_SEND]: isNaN(data.periodWaitToSend)
            ? SR_DEFAULT_PERIOD_WAIT_TO_SEND
            : data.periodWaitToSend,
          [ScheduledReportEditFormFields.BELL_SCHEDULE]: data.bellSchedules || [],
          [ScheduledReportEditFormFields.SIS_TEMPLATE]: data.sisTemplate,
          [ScheduledReportEditFormFields.BEHAVIOR_CODE]: data.behaviorCode || '',
        });

        if (data.email) {
          this.emailOptions = data.email.map(email => ({
            label: email,
            value: email,
          }));
        }

        if (data.reportType) {
          this.form.get(this.FORM_FIELDS.REPORT_TYPE).setValue(data.reportType);
          this._lastReportType = data.reportType;
          this._configFrequencyOptions();
          this._updateSisTemplateValidators(data.reportType);
        }

        this._defaultFilterValuesSubj.next({
          reportType: data.reportType as any,
          filters: data.filters || ({} as any),
        });

        this.form.markAsPristine();
        this.form.markAsUntouched();

        this._setUpFormSubscriptions();
      },
      onValidate: data => {
        // mark all the fields as touched
        Object.values(this.form.controls).forEach(control => {
          control.markAsTouched();
          control.updateValueAndValidity();
        });

        // filters sub section needs to be marked as touched
        this._cdr.markForCheck();

        return this.form.valid;
      },
      onSuccess: (type, data) => {
        if (type === 'delete') {
          this._modalRef.close(ModalOverlayServiceCloseEventType.DELETE, {
            deleted: data.id,
          });
        }

        if (type === 'create') {
          this._modalRef.close(ModalOverlayServiceCloseEventType.CREATE, {
            created: data,
          });
        }

        if (type === 'update') {
          this._modalRef.close(ModalOverlayServiceCloseEventType.SUBMIT, {
            updated: data,
          });
        }
      },
      onSubmit: data => {
        const active = !data.id ? true : data.active;

        const report: Partial<ScheduledReport> = {
          ...(data || {}),
          ...this.form.value,
          active,
        };

        return report;
      },
      onShowLoader: promise => rootService.addLoadingPromise(promise),
      onCancel: async () => {
        if (this.form.pristine) {
          this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
          return;
        }

        const modalRef = await this._systemAlertModal.open({
          modalType: SystemAlertModalType.WARNING,
          heading: ScheduledReportEditMessage.DELETE_CONFIRM_DISCARD_TITLE,
          message: ScheduledReportEditMessage.DELETE_CONFIRM_DISCARD_DESC,
          closeBtn: ScheduledReportEditMessage.DELETE_CONFIRM_CANCEL_BTN,
          confirmActionBtn: ScheduledReportEditMessage.DELETE_CONFIRM_DISCARD_BTN,
        });

        const response = await modalRef.afterClosed().toPromise();

        if (response?.type === SystemAlertCloseEvents.CONFIRM) {
          this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
        }
      },
      onDelete: async () => {
        const modalRef = await this._systemAlertModal.open({
          modalType: SystemAlertModalType.WARNING,
          heading: ScheduledReportEditMessage.DELETE_CONFIRM_TITLE,
          closeBtn: ScheduledReportEditMessage.DELETE_CONFIRM_CANCEL_BTN,
          confirmActionBtn: ScheduledReportEditMessage.DELETE_CONFIRM_DELETE_BTN,
        });

        const response = await modalRef.afterClosed().toPromise();

        return response?.type === SystemAlertCloseEvents.CONFIRM;
      },
      onFormStateChange: state => {
        if (state === 'invalid') {
          // scroll to top of form
          scrollIntoView(this.crudForm.nativeElement, {
            align: { top: 0 },
          });
        }
      },
    });
    this.isNew = !this.dialogData?.id;
  }

  ngOnInit(): void {
    this.initFields();
  }

  ngAfterViewInit(): void {
    if (this.isNew) {
      // need to wait till ngSwitch is done for setting these so wait till after view init
      this._setDefaultFilters();
    }
  }
  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  async initFields() {
    this._setFormState('loading');
    await this.init(this.dialogData?.id);
    await this._initBellScheduleOptions();
    this._setFormState('idle');
    this._cdr.markForCheck();
  }

  public addNewEmail(email: string) {
    return { label: email, value: email };
  }

  private _setUpFormSubscriptions() {
    this.form
      .get(this.FORM_FIELDS.FREQUENCY)
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(value => {
        if (this._lastFrequencyType !== value) this._onFrequencyChange(value);
      });

    this.form
      .get(this.FORM_FIELDS.DELIVERY_TYPE)
      .valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(value => {
        this._onDeliveryMethodChange(value);
      });

    this.form
      .get(this.FORM_FIELDS.REPORT_TYPE)
      .valueChanges.pipe(
        takeUntil(this._destroyed$),
        filter(r => r !== this._lastReportType), // only update if report type has changed
      )
      .subscribe(value => {
        this._onReportTypeChange(value);
      });
  }

  private async _initBellScheduleOptions() {
    if (await this._bellSchedulePermService.isBellScheduleEnabledForCurrentUser()) {
      this._setFormState('loading');
      const schedules = await this._bellScheduleService.fetchAll();
      this.bellScheduleOptions$ = of(
        schedules.map(schedule => ({
          label: schedule.name,
          value: schedule.id,
        })),
      );
      this._setFormState('idle');
    }
  }

  private _onFrequencyChange(value: ScheduledReportFrequency) {
    this._lastFrequencyType = value;
    this._updateDayValidators(value);
    this._updatePerPeriodValidators(value);
  }

  private _updatePerPeriodValidators(value: ScheduledReportFrequency) {
    const periodWaitControl = this.form.get(this.FORM_FIELDS.PERIOD_WAIT_TO_SEND);
    const bellScheduleControl = this.form.get(this.FORM_FIELDS.BELL_SCHEDULE);

    bellScheduleControl.setValue([]);

    if (value === ScheduledReportFrequency.Periodic) {
      periodWaitControl.setValidators([Validators.required, Validators.min(0)]);
      periodWaitControl.setValue(SR_DEFAULT_PERIOD_WAIT_TO_SEND);
      bellScheduleControl.setValidators([Validators.required]);
    } else {
      periodWaitControl.clearValidators();
      periodWaitControl.setValue(null);
      bellScheduleControl.clearValidators();
    }

    periodWaitControl.updateValueAndValidity();
    bellScheduleControl.updateValueAndValidity();
  }

  private _onDeliveryMethodChange(value: string[]) {
    const val = value || [];
    this._updateEmailValidators(val);
    this._updateSftpValidators(val);
  }

  private _onReportTypeChange(reportType) {
    this._lastReportType = reportType;
    this._defaultFilterValuesSubj.next({
      reportType,
      filters: {} as any,
    });
    this.form.get(this.FORM_FIELDS.BEHAVIOR_CODE).setValue('');
    this.form.get(this.FORM_FIELDS.SIS_TEMPLATE).setValue(null);
    this._configFrequencyOptions();
    this._updateSisTemplateValidators(reportType);
    if (reportType === ScheduledReportType.PBIS_TARDY) this._setDefaultTardyFilter();
  }

  private async _configFrequencyOptions() {
    const hasBellSchedule = await this._bellSchedulePermService.isBellScheduleEnabledForCurrentUser();
    if (
      hasBellSchedule &&
      (this._lastReportType === ReportTypes.PBIS_TARDY || this._lastReportType === ReportTypes.CHECKIN_TARDY)
    ) {
      this._frequencyOptSubj.next([PERIODIC_FREQUENCY_OPTION, ...NON_PERIODIC_FREQUENCY_OPTIONS]);
    } else {
      this._frequencyOptSubj.next(NON_PERIODIC_FREQUENCY_OPTIONS);
      if (this.form.get(this.FORM_FIELDS.FREQUENCY).value === ScheduledReportFrequency.Periodic) {
        this.form.get(this.FORM_FIELDS.FREQUENCY).setValue(ScheduledReportFrequency.Weekly);
      }
    }
  }

  private _updateEmailValidators(value: string[]) {
    const emailControl = this.form.get(this.FORM_FIELDS.EMAIL);
    if (value.includes('email')) {
      emailControl.setValidators([Validators.required]);
    } else {
      emailControl.setValue([]);
      emailControl.clearValidators();
    }

    emailControl.updateValueAndValidity();
  }

  private _updateDayValidators(value: ScheduledReportFrequency) {
    const dayControl = this.form.get(this.FORM_FIELDS.DAY);
    if (value === ScheduledReportFrequency.Weekly) {
      dayControl.setValidators([Validators.required]);
    } else {
      dayControl.clearValidators();
    }

    dayControl.updateValueAndValidity();
  }

  private _updateSftpValidators(value: string[]) {
    const host = this.form.get(this.FORM_FIELDS.SFTP_HOST);
    const username = this.form.get(this.FORM_FIELDS.SFTP_USER_NAME);
    const password = this.form.get(this.FORM_FIELDS.SFTP_PASSWORD);
    if (value.includes('sftp')) {
      host.setValidators([Validators.required]);
      username.setValidators([Validators.required]);

      // edit we don't prepopulate the password again so it's not required on update
      if (this.isNew) {
        password.setValidators([Validators.required]);
      }
    } else {
      host.setValue('');
      host.clearValidators();
      username.clearValidators();
      username.setValue('');
      password.clearValidators();
      password.setValue('');
    }

    host.updateValueAndValidity();
    username.updateValueAndValidity();
    password.updateValueAndValidity();
  }

  private _updateSisTemplateValidators(value: string) {
    const sisTemplateControl = this.form.get(this.FORM_FIELDS.SIS_TEMPLATE);
    if (value === ScheduledReportType.PBIS_TARDY || value === ScheduledReportType.CHECKIN_TARDY) {
      sisTemplateControl.setValidators([Validators.required]);
    } else {
      sisTemplateControl.clearValidators();
      sisTemplateControl.setValue(null);
    }

    sisTemplateControl.updateValueAndValidity();
  }

  private _setDefaultFilters() {
    const defaultFilterValues = this.dialogData?.prepopulatedFields || {
      reportType: ScheduledReportType.PBIS_HISTORY,
      filters: {} as BmReportsFilters,
    };
    this._defaultFilterValuesSubj.next(defaultFilterValues as any);
  }

  private async _setDefaultTardyFilter() {
    const tardyType = await this._pbisService.getTardyTypeId();
    this._defaultFilterValuesSubj.next({
      reportType: ReportTypes.PBIS_TARDY,
      filters: {
        types: [tardyType],
      } as BmReportsFilters,
    });
  }
}
